/**
 * 69.9, base point defence (the owner, Oct 20: "hqs are starting to get pretty valuable which is good, but air strike could
 * conceivably just wipe out a base. So we'll probably want some base-based anti-air and anti-artillery systems that we can
 * play with to make bases more defensible from long range threats, will also probably be early AA systems"; and on the
 * note: "a CIWS and CRAM variant … placed with the base for now … kill chances … depends on the incoming somehow … Lean yes
 * for strike interception. CIWS and CRAM should be super range limited, and also limited by some sort of radar vis system
 * that allows them to resolve targets and is constrained by terrain / horizon"; BUILDLIST 69.9; with GameConfig.pointDefence).
 *
 * **The mount** is a structure of type `pointDefence` placed with each base (match/setup.ts: one of each variant in
 * POINT_DEFENCE.perBase to either side of the HQ), its variant and its magazine in `StructureState.pd`; its radar head and
 * its gun are parts that act (STRUCTURE_PARTS: the head gone, it is blind; the gun worn, it fires slower and less surely;
 * gone, silent). **What it engages** are the enemy's rounds in flight that carry a burst — the gun pits' shells
 * (`state.shells`) and the blast bullets (`state.bullets` with `blast`: rockets, the 40 mm, the airstrike's rounds) — and
 * the enemy's low ships (69.3b's flights, by their pose). **The radar** resolves a target that is within its range, above
 * the mount's minimum elevation, coming toward it (a round) and in its line of sight from the head (`personLineOfSight`:
 * the ground's coarse mesh and fine relief and the horizon — a round behind a ridge or under the horizon is not resolved
 * until it clears); it scans every `scanEvery` ticks and **tracks** the target soonest to arrive (the range over the
 * closing speed) among the targets it tracks — every resolved one at once, each from when it was first resolved; a target must stay tracked `track` seconds before the first burst at it. **A burst** every `interval`: against
 * a round, a kill by a chance — the kind's base × the round's size (its blast radius) × its speed × its crossing angle × its
 * range × the gun's wear (`killChance`) — rolled once per burst, keyed by the match seed, the mount and the tick; a killed
 * round leaves its list that tick (no impact, no burst on the ground: `intercepted`); against a ship, `flak` damage through
 * the ship body (`shipBlow`, the nearest part within reach). The magazine counts bursts; empty, it refills `refill` seconds
 * later. Pure and deterministic; nothing runs without a mount (the Match's guard), and without the switch no mount is placed.
 */
import type { GameState, StructureState, Vec3, ActionShell, Bullet, DropshipFlight } from '../types';
import { POINT_DEFENCE, type PointDefenceVariant } from '../constants/action';
import { PHYS_DT } from '../constants/physics';
import { DAMAGE_VS } from '../constants/damage';
import { SeededRandom } from './random';
import { structureTeam } from './body';
import { partHealth, roleLost, gunWear, markedParts } from './parts';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { personSurfaceRadius, personLineOfSight } from './fineTerrain';
import { flightNow, lighterNow } from './shipNow';
import { LIGHTER_ID_BASE } from './lighterBody';
import { shipBlow } from './shipDamage';
import { supplyDroneTargets, hitSupplyDrone, SUPPLY_DRONE_ID_BASE } from './supplyDrone';
import { launchSiteMissile, irReachShare } from './antiDropshipMissile';
import { structureFrame } from './hits';
import { blastOwnerTeam } from './explosions';
import { AIR_DEFENCE_SITE } from '../constants/action';
import type { MissileFlight } from '../types';
import { vecAdd, vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';
import { dsin } from './dmath';
import { defaultOn, towedGunsOn } from './defaultOn';

export type RoundKind = 'shell' | 'bomb' | 'rocket' | 'grenade';
export type PdTargetKind = RoundKind | 'ship';
export interface PdTarget { kind: PdTargetKind; id: number; pos: Vec3; vel: Vec3; key: string; blastRadius: number }
/** The gun mounts' variants (69.50 step 2a: the site launches missiles, never a burst). */
export type GunVariant = Exclude<PointDefenceVariant, 'site'>;
export type PdEvent =
  | { kind: 'pdBurst'; mountId: number; variant: GunVariant; planetIndex: number; at: Vec3; target: { kind: PdTargetKind; id: number }; hit: boolean }
  | { kind: 'intercepted'; mountId: number; variant: PointDefenceVariant; planetIndex: number; at: Vec3; target: { kind: RoundKind; id: number } }
  /** 69.50 step 2a: an air-defence site's missile away at a ship (its flight in `state.missiles`) */
  | { kind: 'siteLaunch'; mountId: number; planetIndex: number; at: Vec3; target: number; missile: MissileFlight };
/** The radar's sight: what resolves a target from the head (tests stub it). */
export type Sight = (a: Vec3, b: Vec3, state: GameState) => boolean;

/** A blast bullet's kind for the table: the strike's bomb by its class, a 40 mm grenade by its class, else a rocket. */
export function roundKind(b: Pick<Bullet, 'explosion' | 'blast' | 'gravity' | 'coast'>): RoundKind | null {
  if (!b.blast) return null;
  if (b.explosion === 'bomb' || b.explosion === 'strikeCharge') return 'bomb';
  if (b.explosion === 'gl40' || b.explosion === 'handGrenade' || b.explosion === 'cannon20') return 'grenade';   // (277: the 20 mm's round as the 30 mm's, which bursts as the 40 mm)
  return 'rocket';
}

/** The variant's table. */
export const variantOf = (m: Pick<StructureState, 'pd'>) => POINT_DEFENCE.variants[m.pd?.variant ?? 'cram'];
/** 69.50 step 2a: the structure is a forward air-defence site (a point-defence structure of variant `site`). */
export const isSite = (m: Pick<StructureState, 'pd'>): boolean => m.pd?.variant === 'site';
/** 69.50 step 2b: a site is powered — no generator named, or its generator standing (the truth: what the site's own power is). */
export function sitePowered(state: { readonly structures: readonly StructureState[] }, m: Pick<StructureState, 'pd'>): boolean {
  const g = m.pd?.power;
  if (g === undefined) return true;
  const s = state.structures.find(x => x.id === g);
  return !!s && s.health > 0;
}
/** 69.50 step 2a: the point-defence systems run — the bases' mounts (GameConfig.pointDefence) or the forward sites (airDefenceSites). */
export const pdOn = (config: Pick<GameState['config'], 'pointDefence' | 'airDefenceSites' | 'clock'>): boolean => !!(defaultOn(config, 'pointDefence') || config.airDefenceSites);

/** The radar head's point in the world (the mount's eye, POINT_DEFENCE.eyePeople over the ground under it; 69.50 step 2a: a site's on its mast, its variant's `eye`). */
export function radarEye(state: Pick<GameState, 'planets' | 'config'>, m: Pick<StructureState, 'planetIndex' | 'surfaceDir'> & Partial<Pick<StructureState, 'pd'>>): Vec3 {
  const planet = state.planets[m.planetIndex], ps = personSetOf(state), dir = vecNormalize(m.surfaceDir);
  const v = variantOf(m), eye = 'eye' in v ? v.eye : POINT_DEFENCE.eyePeople;
  return vecAdd(planet.position, vecScale(dir, personSurfaceRadius(planet, dir, ps) + eye * PERSON_BODY_HEIGHT * ps.k));
}

/** 69.50 step 2a: where a site's missile leaves — its launcher's cells, AIR_DEFENCE_SITE.launcher ahead of the mast along the site's facing (its yaw) and up. */
export function siteLauncher(state: Pick<GameState, 'planets' | 'config'>, m: Pick<StructureState, 'planetIndex' | 'surfaceDir'> & Partial<Pick<StructureState, 'yaw'>>): Vec3 {
  const planet = state.planets[m.planetIndex], ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k, up = vecNormalize(m.surfaceDir);
  const { tz } = structureFrame(m, up), L = AIR_DEFENCE_SITE.launcher;
  return vecAdd(vecAdd(planet.position, vecScale(up, personSurfaceRadius(planet, up, ps) + L.height * person)), vecScale(tz, L.forward * person));
}

/**
 * The chance a burst from a mount of `variant` kills a round of `kind` with `blastRadius` (people) at `speed` (people a second)
 * crossing at `crossCos` (the cosine between its velocity and the line to the mount: 1 head-on) at `range` (people), the gun
 * worn `wear` (0 … 1): the kind's base × size × speed × crossing × range × (1 − wear), each factor clamped to its band.
 */
export function killChance(variant: PointDefenceVariant, kind: RoundKind, blastRadius: number, speed: number, crossCos: number, range: number, wear: number, withGuns = false): number {
  const v = POINT_DEFENCE.variants[variant], P = POINT_DEFENCE, base = v.kill[kind] * (withGuns ? P.withGuns.killScale[kind] ?? 1 : 1);   // (69.13 (c): the towed guns' tuning)
  const clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x));
  const size = clamp(blastRadius / P.size.ref, P.size.min, P.size.max);
  const sp = clamp(P.speed.ref / Math.max(1e-6, speed), P.speed.min, P.speed.max);
  const crossing = P.crossing.broadside + (1 - P.crossing.broadside) * clamp(Math.abs(crossCos), 0, 1);
  const r = range <= v.reach / 2 ? 1 : 1 - (1 - P.range.edge) * clamp((range - v.reach / 2) / (v.reach / 2), 0, 1);
  return clamp(base * size * sp * crossing * r * (1 - wear), 0, 1);
}

/** The mount's team (its owner's). */
const teamOf = (state: GameState, m: StructureState): number | null => structureTeam(state, m);
// (69.64: a strike's round, `-1 - team`, is its side's — before, no one's, so a side's own mounts shot at its own rounds)
const ownerTeam = (state: GameState, ownerId: number): number | null => blastOwnerTeam(state, ownerId) ?? null;

/**
 * What a mount's radar resolves at `tick`: the enemy's rounds and ships within its radar range, above its minimum elevation,
 * coming toward it (a round), and in its line of sight from the head — each with its position, velocity and a key; and the
 * one soonest to arrive first (the range over the closing speed; a ship by its range).
 */
export function pdCandidates(state: GameState, m: StructureState, tick: number, sight: Sight = personLineOfSight, probe = false): PdTarget[] {
  const v = variantOf(m), ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k, team = teamOf(state, m);
  const eye = radarEye(state, m), up = vecNormalize(m.surfaceDir), radar = v.radar * person, sinMin = dsin(v.minElevation);
  const out: Array<PdTarget & { eta: number }> = [];
  // 69.64 (DECISIONS 288, 233): with the towed guns, a gun mount sees the strike's rounds `withGuns.radarBomb` × as far (their
  // rounds close at 450–960 people a second: through the plain radar in a fifth of a second, under any track)
  const bombRadar = !('shipsOnly' in v) && towedGunsOn(state.config) ? radar * POINT_DEFENCE.withGuns.radarBomb : radar;
  const consider = (kind: PdTargetKind, id: number, pos: Vec3, vel: Vec3, blastRadius: number, key: string) => {
    const rel = vecSub(pos, eye), range = vecLength(rel);
    if (range > (kind === 'bomb' ? bombRadar : radar) || range < 1e-9) return;
    if (vecDot(rel, up) / range < sinMin) return;   // (under the mount's elevation floor: not its target)
    const closing = kind === 'ship' ? 1 : -vecDot(vel, rel) / range;   // (a round: coming this way)
    if (kind !== 'ship' && !(closing > 0)) return;
    if (!sight(eye, pos, state)) return;   // (the ground and the horizon: not resolved till it clears)
    out.push({ kind, id, pos, vel, key, blastRadius, eta: kind === 'ship' ? range / person : range / Math.max(1e-6, closing) });
  };
  // (69.50 step 2a: a site's targets are ships; the radar picture's probe round asks its sight alone — 2b: with siteRounds a
  // site takes the artillery's and the mortars' rounds too, a shell or a strike round, never a rocket or a grenade)
  const site = 'shipsOnly' in v, rounds = probe || !site || !!state.config.siteRounds;
  const roundOk = (k: RoundKind) => probe || !site || AIR_DEFENCE_SITE.roundKill[k] !== undefined;
  if (rounds) for (const sh of state.shells ?? []) if (ownerTeam(state, sh.ownerId) !== team) consider('shell', sh.id, sh.pos, sh.vel, POINT_DEFENCE.size.ref, `s${sh.id}`);
  if (rounds) for (const b of state.bullets ?? []) { const k = roundKind(b); if (k && roundOk(k) && ownerTeam(state, b.ownerId) !== team) consider(k, b.id, b.pos, b.vel, (b.blast!.radius) / person, `b${b.id}`); }
  for (const f of state.dropships?.flights ?? []) {
    if (f.team === team || f.planetIndex !== m.planetIndex) continue;
    const now = flightNow(state, f, tick);   // 69.17 step 1: the ship as it is now
    if (!now) continue;
    consider('ship', f.id, now.pos, { x: 0, y: 0, z: 0 }, 0, `f${f.id}`);
  }
  for (const r of site ? [] : state.lighters?.runs ?? []) {   // 69.10 step 4: a lighter in the envelope is a ship (69.50 step 2b, DECISIONS 265: never a site's — "these would defend lighter airspace")
    if (r.team === team || r.planetIndex !== m.planetIndex) continue;
    const now = lighterNow(state, r, tick);
    if (!now) continue;
    consider('ship', LIGHTER_ID_BASE + r.id, now.pos, { x: 0, y: 0, z: 0 }, 0, `l${r.id}`);
  }
  // 69.32 step 3: an enemy supply drone in the envelope is a low ship to a mount (never a site's: the owner's split, the sites for ships)
  for (const x of site ? [] : supplyDroneTargets(state, tick)) {
    if (x.run.team === team || x.run.planetIndex !== m.planetIndex) continue;
    consider('ship', SUPPLY_DRONE_ID_BASE + x.run.id, x.pos, { x: 0, y: 0, z: 0 }, 0, `d${x.run.id}`);
  }
  out.sort((a, b) => a.eta - b.eta || a.key.localeCompare(b.key));
  return out;
}

/** The mount can engage: alive, its radar head and gun not gone, a magazine (or its refill due). */
function ready(state: GameState, m: StructureState, tick: number): { m: StructureState; can: boolean } {
  if (m.health <= 0 || !m.pd) return { m, can: false };
  if (isSite(m) && !sitePowered(state, m)) return { m, can: false };   // (69.50 step 2b: its generator down, the site is silent)
  if (state.config.partialDestruction && markedParts(m.type).length && (partHealth(m, 0) <= 0 || partHealth(m, 1) <= 0)) return { m, can: false };   // (the radar head or the gun gone)
  if (m.pd.magazine <= 0) {
    if (m.pd.refillAt !== undefined && tick >= m.pd.refillAt) { const { refillAt: _r, ...pd } = m.pd; return { m: { ...m, pd: { ...pd, magazine: variantOf(m).magazine } }, can: true }; }
    return { m, can: false };
  }
  return { m, can: true };
}

/**
 * The point defence's tick: each mount (in id order) scans on its beat, keeps or takes its track, and fires when the track has
 * held long enough and its interval is up — a round killed leaves its list, a ship takes flak. The state and the events.
 */
export function stepPointDefence(state: GameState, tick: number, sight: Sight = personLineOfSight): { state: GameState; events: PdEvent[] } {
  const mounts = state.structures.filter(s => s.type === 'pointDefence').sort((a, b) => a.id - b.id);
  if (!mounts.length) return { state, events: [] };
  const events: PdEvent[] = [];
  let next = state;
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  for (const m0 of mounts) {
    const r = ready(next, m0, tick);
    let m = r.m;
    if (!r.can) { if (m !== m0) next = { ...next, structures: next.structures.map(s => (s.id === m.id ? m : s)) }; continue; }
    const v = variantOf(m), trackTicks = Math.round(v.track / PHYS_DT), intervalTicks = Math.round(v.interval / PHYS_DT);
    const tuned = !isSite(m) && towedGunsOn(next.config), guns = tuned ? POINT_DEFENCE.withGuns.guns : v.guns;   // (69.13 (c), DECISIONS 233: the tuning travels with the towed guns — the bases' mounts', not a forward site's (300: its one missile at a ship))
    const pd = { ...m.pd! };
    // the radar tracks every target it resolves (a scan every scanEvery ticks: each track's `since` kept while its target stays
    // resolved, dropped when it is gone — a stick of rounds is tracked together, not one after the other); between scans the
    // tracked targets are read where they are now
    const scan = (tick + m.id) % POINT_DEFENCE.scanEvery === 0 || !pd.tracks?.length;
    const cands = pdCandidates(next, m, tick, scan ? sight : () => true);
    const old = new Map((pd.tracks ?? []).map(t => [t.key, t.since]));
    const tracks = (scan ? cands : cands.filter(c => old.has(c.key))).map(c => ({ key: c.key, since: old.get(c.key) ?? tick })).sort((a, b) => a.key.localeCompare(b.key));
    if (tracks.length) pd.tracks = tracks; else delete pd.tracks;
    // the gun: at the mature track soonest to arrive within its reach, when its interval is up — 69.13 (c): `guns` of them a
    // mount, each a burst off the magazine at the soonest such round still flying (two on one round when it is alone in reach:
    // a salvo's rounds come half a second apart, so one is in a 60-person reach at a time)
    const eye = radarEye(next, m), due = tick >= (pd.nextBurst ?? 0), killed = new Set<string>();
    const site = isSite(m), chased = site ? new Set((next.missiles?.live ?? []).filter(x => x.site === m.id && x.target !== null).map(x => x.target!)) : null;
    // (69.50 step 2b: a site's ships before its rounds — what it is for; the mounts take the soonest to arrive of all)
    const order = site ? [...cands.filter(c => c.kind === 'ship'), ...cands.filter(c => c.kind !== 'ship')] : cands;
    const soonest = (): { target: PdTarget; range: number; rel: Vec3 } | null => {
      for (const c of order) {
        if (killed.has(c.key)) continue;
        if (c.kind === 'ship' && chased?.has(c.id)) continue;   // (69.50 step 2a: a site's one missile at a ship at a time)
        const since = old.get(c.key) ?? tick;
        if (tick - since < trackTicks) continue;
        const r0 = vecSub(c.pos, eye), rg = vecLength(r0) / person;
        if (rg > v.reach * (site && c.kind === 'ship' ? irReachShare(next, c.id) : 1)) continue;   // (69.59 step 3: a site's IR missile locks a suppressed helicopter nearer)
        return { target: c, range: rg, rel: r0 };   // (cands are in eta order)
      }
      return null;
    };
    for (let g = 0; due && g < guns && (g === 0 || pd.magazine > 0); g++) {
      const pick = soonest();
      if (!pick) break;
      const { target, range, rel } = pick;
      {
        const rng = new SeededRandom(`${next.config.seed ?? ''}-pd-${m.id}-${tick}${g > 0 ? `-g${g}` : ''}`);
        const wear = gunWear(next, { type: m.type, parts: m.parts, health: m.health });
        pd.magazine -= 1;
        if (g === 0) { pd.nextBurst = tick + Math.round(intervalTicks * (1 + wear)); pd.aim = target.pos; }
        if (pd.magazine <= 0) pd.refillAt = tick + Math.round(v.refill / PHYS_DT);
        let hit = false;
        if (site && target.kind === 'ship') {
          // 69.50 step 2a: the site's missile, out of its launcher after the ship its radar holds (core/antiDropshipMissile.ts flies it)
          const shot = launchSiteMissile(next, m, teamOf(next, m) ?? -1, target.id, siteLauncher(next, m), target.pos);
          next = shot.state;
          chased?.add(target.id);   // (300: its one missile at a ship — chased from the launch, not the next tick's list)
          events.push({ kind: 'siteLaunch', mountId: m.id, planetIndex: m.planetIndex, at: shot.missile.pos, target: target.id, missile: shot.missile });
          continue;
        }
        if (target.kind === 'ship') {
          const rangeFactor = range <= v.reach / 2 ? 1 : 1 - (1 - POINT_DEFENCE.range.edge) * Math.min(1, (range - v.reach / 2) / (v.reach / 2));
          const flak = v.flak * rangeFactor * (1 - wear);
          next = target.id >= SUPPLY_DRONE_ID_BASE ? hitSupplyDrone(next, target.id - SUPPLY_DRONE_ID_BASE, flak * DAMAGE_VS.flak.light, tick)   // (69.32 step 3: a drone's a light hull's)
            : shipBlow(next, target.id, m.planetIndex, target.pos, flak, 'flak', undefined, 1.5 * person);
          hit = true;
        } else {
          const speed = vecLength(target.vel) / person, crossCos = speed > 1e-9 ? -vecDot(target.vel, rel) / (vecLength(target.vel) * Math.max(1e-9, vecLength(rel))) : 1;
          const p = site ? (AIR_DEFENCE_SITE.roundKill[target.kind] ?? 0) * (1 - wear) : killChance(m.pd!.variant as GunVariant, target.kind, target.blastRadius, speed, crossCos, range, wear, tuned);   // (69.50 step 2b: a site's missile at a round, its kind's chance)
          if (rng.next() < p) {
            hit = true;
            if (target.kind === 'shell') next = { ...next, shells: (next.shells ?? []).filter((s: ActionShell) => s.id !== target.id) };
            else next = { ...next, bullets: (next.bullets ?? []).filter((b: Bullet) => b.id !== target.id) };
            killed.add(target.key);
            events.push({ kind: 'intercepted', mountId: m.id, variant: m.pd!.variant, planetIndex: m.planetIndex, at: target.pos, target: { kind: target.kind, id: target.id } });
            if (pd.tracks) { const left = pd.tracks.filter(t => t.key !== target.key); if (left.length) pd.tracks = left; else delete pd.tracks; }
          }
        }
        if (!site) events.push({ kind: 'pdBurst', mountId: m.id, variant: m.pd!.variant as GunVariant, planetIndex: m.planetIndex, at: target.pos, target: { kind: target.kind, id: target.id }, hit });   // (a site's missile at a round: its back-blast, and the kill's `intercepted`)
      }
    }
    // (69.60: a mount whose block came out as it was is kept — the same object — else the static structures change every
    // tick and every reader memoised on them (the obstacles' filing, the shapes, the footprints) rebuilds: 4.5 ms a tick
    // on the open board at ½ with two sites)
    if (m === m0 && samePd(m0.pd!, pd)) continue;
    m = { ...m, pd };
    next = { ...next, structures: next.structures.map(s => (s.id === m.id ? m : s)) };
  }
  return { state: next, events };
}

/** Two of a mount's blocks alike in every field the step writes (its tracks by key and since). */
function samePd(a: NonNullable<StructureState['pd']>, b: NonNullable<StructureState['pd']>): boolean {
  if (a.magazine !== b.magazine || a.nextBurst !== b.nextBurst || a.refillAt !== b.refillAt || a.aim !== b.aim) return false;
  const ta = a.tracks ?? [], tb = b.tracks ?? [];
  return ta.length === tb.length && ta.every((t, i) => t.key === tb[i].key && t.since === tb[i].since);
}

export type { DropshipFlight };
